/**
 * WebAuthn / passkey helpers.
 *
 * Wraps the browser's navigator.credentials.create() / .get() APIs and
 * serializes ArrayBuffers to base64url so the payload can be sent to the
 * backend as JSON.
 *
 * Also encapsulates the reauth policy timings + local storage keys that
 * the app-shell uses to decide when to prompt the user.
 */
import { API } from '../App';
import apiClient from './apiClient';
import { isNativeApp } from './nativeBridge';

// --- base64url helpers ------------------------------------------------------
const b64urlToBuf = (s) => {
  if (!s) return new ArrayBuffer(0);
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

const bufToB64url = (buf) => {
  const bytes = new Uint8Array(buf);
  let str = '';
  bytes.forEach((b) => { str += String.fromCharCode(b); });
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const credentialToJSON = (cred) => {
  const resp = cred.response;
  const isAttestation = 'attestationObject' in resp;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment || null,
    response: isAttestation
      ? {
          attestationObject: bufToB64url(resp.attestationObject),
          clientDataJSON: bufToB64url(resp.clientDataJSON),
          transports: (resp.getTransports && resp.getTransports()) || [],
        }
      : {
          authenticatorData: bufToB64url(resp.authenticatorData),
          clientDataJSON: bufToB64url(resp.clientDataJSON),
          signature: bufToB64url(resp.signature),
          userHandle: resp.userHandle ? bufToB64url(resp.userHandle) : null,
        },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
};

// --- capabilities -----------------------------------------------------------
// In the native wrapper (React Native WebView) we deliberately report NO
// WebAuthn capability. Android WebView has no WebAuthn support and iOS
// WKWebView needs an Associated-Domains entitlement we don't ship, so any
// PublicKeyCredential call would crash or hang. Native biometric prompts
// flow through `utils/nativeBridge.js` instead.
export const webauthnAvailable = () =>
  typeof window !== 'undefined' &&
  !isNativeApp() &&
  window.PublicKeyCredential &&
  window.isSecureContext;

export const platformAuthenticatorAvailable = async () => {
  if (!webauthnAvailable()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

// --- API helpers ------------------------------------------------------------
export async function hasPasskey(email) {
  if (!email) return false;
  try {
    const r = await fetch(`${API}/auth/webauthn/has-passkey?email=${encodeURIComponent(email)}`, {
      credentials: 'include',
    });
    if (!r.ok) return false;
    const j = await r.json();
    return !!j.has_passkey;
  } catch {
    return false;
  }
}

// --- Enrollment (requires an active session) -------------------------------
export async function enrollPasskey(deviceLabel) {
  if (!webauthnAvailable()) throw new Error('This browser does not support passkeys.');

  const optsRes = await apiClient.post('/auth/webauthn/register-options');
  const opts = optsRes.data;

  const publicKey = {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    user: { ...opts.user, id: b64urlToBuf(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
      ...c, id: b64urlToBuf(c.id),
    })),
  };

  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error('No credential returned by the browser.');

  const verifyRes = await apiClient.post('/auth/webauthn/register-verify', {
    credential: credentialToJSON(cred),
    device_label: deviceLabel || null,
  });
  return verifyRes.data;
}

// --- Client-side error telemetry -------------------------------------------
// Fire-and-forget POST to the backend so we get a full trace of every
// biometric failure without asking the user to open dev tools.
export function reportBiometricError({ stage, error, email, context } = {}) {
  try {
    const body = {
      stage: stage || 'unknown',
      error_name: String(error?.name || ''),
      error_message: String(error?.message || ''),
      user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
      email: email || '',
      is_pwa: typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches,
      is_native: !!(typeof window !== 'undefined' && window.NativeApp?.isNative),
      context: context || {},
    };
    // Use sendBeacon when available so the report survives even if the
    // page navigates away right after the failure.
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      navigator.sendBeacon(`${API}/auth/webauthn/report-error`, blob);
      return;
    }
    fetch(`${API}/auth/webauthn/report-error`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* noop */ });
  } catch { /* noop */ }
}

// --- Login with passkey (no session required) ------------------------------

// Two-step API: prepare returns a `verify` fn bound to the pre-fetched
// challenge. Callers can prepare eagerly (e.g. when the lock screen is
// shown) and call `get()` + `verify()` synchronously inside the click
// handler. This is critical on iOS Safari, which enforces a ~5s user
// activation window - if a network round trip pushes
// `navigator.credentials.get()` past that window, the OS refuses to
// show the Face ID prompt at all (silent NotAllowedError).
export async function prepareLoginWithPasskey(email) {
  if (!webauthnAvailable()) {
    const err = new Error('This browser does not support passkeys.');
    reportBiometricError({ stage: 'capability', error: err, email });
    throw err;
  }

  let optsRes;
  try {
    optsRes = await fetch(`${API}/auth/webauthn/login-options`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch (netErr) {
    reportBiometricError({ stage: 'options', error: netErr, email });
    throw new Error('Could not start passkey sign-in.');
  }
  if (!optsRes.ok) {
    reportBiometricError({ stage: 'options', error: new Error(`login-options ${optsRes.status}`), email, context: { status: optsRes.status } });
    throw new Error('Could not start passkey sign-in.');
  }
  const opts = await optsRes.json();
  const publicKey = {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({
      ...c, id: b64urlToBuf(c.id),
    })),
  };
  const preparedAt = Date.now();

  const verify = async (cred) => {
    if (!cred) {
      const err = new Error('No credential returned by the browser.');
      reportBiometricError({ stage: 'get', error: err, email });
      throw err;
    }
    const verifyRes = await fetch(`${API}/auth/webauthn/login-verify`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential: credentialToJSON(cred) }),
    });
    if (!verifyRes.ok) {
      const j = await verifyRes.json().catch(() => ({}));
      const detail = j.detail || 'Biometric sign-in failed. Try again.';
      reportBiometricError({
        stage: 'verify',
        error: new Error(detail),
        email,
        context: { status: verifyRes.status, detail, preparedAgeMs: Date.now() - preparedAt },
      });
      throw new Error(detail);
    }
    return verifyRes.json();
  };

  return { publicKey, verify, opts, preparedAt };
}

// One-shot login (backward compatible - used from AuthPage where the
// button click IS the trigger and network latency inside the click is
// acceptable in browser tabs).
export async function loginWithPasskey(email) {
  const prep = await prepareLoginWithPasskey(email);
  let cred;
  try {
    cred = await navigator.credentials.get({ publicKey: prep.publicKey });
  } catch (getErr) {
    reportBiometricError({
      stage: 'get',
      error: getErr,
      email,
      context: {
        allowCredentialsCount: (prep.opts.allowCredentials || []).length,
        rpId: prep.opts.rpId,
        preparedAgeMs: Date.now() - prep.preparedAt,
      },
    });
    throw getErr;
  }
  return prep.verify(cred);
}

// --- List / delete credentials ---------------------------------------------
export async function listPasskeys() {
  const r = await apiClient.get('/auth/webauthn/credentials');
  return r.data || [];
}
export async function deletePasskey(credentialId) {
  await apiClient.delete(`/auth/webauthn/credentials/${encodeURIComponent(credentialId)}`);
}
export async function renamePasskey(credentialId, deviceLabel) {
  const r = await apiClient.patch(
    `/auth/webauthn/credentials/${encodeURIComponent(credentialId)}`,
    { device_label: deviceLabel },
  );
  return r.data;
}

// --- Reauth policy timings + local flags -----------------------------------
export const REAUTH = {
  BACKGROUND_LOCK_MS: 30 * 60 * 1000,        // 30 min
  IDLE_STEP_UP_MS: 14 * 24 * 60 * 60 * 1000, // 14 days -> biometric on next open
  IDLE_FULL_MS: 30 * 24 * 60 * 60 * 1000,    // 30 days -> full password
  STEP_UP_FRESH_MS: 5 * 60 * 1000,           // sensitive actions require biometric within 5 min
};

const LS = {
  ENROLL_OFFERED_COUNT: 'akf_biometric_enroll_offered_count',
  LAST_UNLOCK_AT: 'akf_biometric_last_unlock_at',
  LAST_STEP_UP_AT: 'akf_biometric_last_step_up_at',
  LAST_ACTIVE_AT: 'akf_last_active_at',
  ENROLL_DISMISSED: 'akf_biometric_enroll_dismissed',
};

export const markUnlockedNow = () => {
  try { localStorage.setItem(LS.LAST_UNLOCK_AT, String(Date.now())); } catch {} // eslint-disable-line no-empty
};
export const markStepUpNow = () => {
  try { localStorage.setItem(LS.LAST_STEP_UP_AT, String(Date.now())); } catch {} // eslint-disable-line no-empty
};
export const markActiveNow = () => {
  try { localStorage.setItem(LS.LAST_ACTIVE_AT, String(Date.now())); } catch {} // eslint-disable-line no-empty
};

export const getEnrollOfferedCount = () => {
  try { return Number(localStorage.getItem(LS.ENROLL_OFFERED_COUNT) || '0'); } catch { return 0; }
};
export const incrementEnrollOfferedCount = () => {
  try {
    const n = getEnrollOfferedCount() + 1;
    localStorage.setItem(LS.ENROLL_OFFERED_COUNT, String(n));
    return n;
  } catch { return 0; }
};
export const isEnrollDismissed = () => {
  try { return localStorage.getItem(LS.ENROLL_DISMISSED) === '1'; } catch { return false; }
};
export const dismissEnrollForever = () => {
  try { localStorage.setItem(LS.ENROLL_DISMISSED, '1'); } catch {} // eslint-disable-line no-empty
};

// Should the app show the "unlock with biometric" screen on foreground?
export const needsBackgroundUnlock = () => {
  try {
    const last = Number(localStorage.getItem(LS.LAST_UNLOCK_AT) || '0');
    if (!last) return false;
    return Date.now() - last > REAUTH.BACKGROUND_LOCK_MS;
  } catch { return false; }
};

// Is a recent step-up biometric available for a sensitive action?
export const hasFreshStepUp = () => {
  try {
    const last = Number(localStorage.getItem(LS.LAST_STEP_UP_AT) || '0');
    if (!last) return false;
    return Date.now() - last < REAUTH.STEP_UP_FRESH_MS;
  } catch { return false; }
};
